import { useState } from "react";
import { useForm, Link as InertiaLink } from "@inertiajs/react";
import {
  Card,
  CardBody,
  CardHeader,
  Input,
  Button,
  Link,
  Chip,
  Tooltip,
} from "@heroui/react";
import Layout from "../Layouts/Layout";

export default function New({ api_key = null, errors = [] }) {
  const isEditing = !!api_key;
  const [showApiKey, setShowApiKey] = useState(false);

  const { data, setData, post, put, processing } = useForm({
    provider: api_key?.provider || "",
    url: api_key?.url || "",
    ai_model_name: api_key?.ai_model_name || "",
    api_key: "",
    nickname: api_key?.nickname || "",
  });

  const handleSubmit = (e) => {
    e.preventDefault();

    if (isEditing) {
      put(`/api-keys/${api_key.id}`, {
        data: {
          user_api_key: data,
        },
      });
    } else {
      post("/api-keys", {
        data: {
          user_api_key: data,
        },
      });
    }
  };

  const handlePreset = (provider) => {
    switch (provider) {
      case "openai":
        setData("provider", "OpenAI");
        setData("url", "https://api.openai.com/v1/chat/completions");
        setData("ai_model_name", "gpt-4o");
        break;
      case "anthropic":
        setData("provider", "Anthropic");
        setData("url", "https://api.anthropic.com/v1/messages");
        setData("ai_model_name", "claude-sonnet-4-20250514");
        break;
      case "groq":
        setData("provider", "Groq");
        setData("url", "https://api.groq.com/openai/v1/chat/completions");
        setData("ai_model_name", "llama-3.1-8b-instant");
        break;
      case "openrouter":
        setData("provider", "OpenRouter");
        setData("url", "https://openrouter.ai/api/v1/chat/completions");
        setData("ai_model_name", "meta-llama/llama-3.2-3b-instruct:free");
        break;
    }
  };

  return (
    <Layout>
      <div className="max-w-3xl mx-auto">
        <div className="mb-8">
          <Link
            as={InertiaLink}
            href="/api-keys"
            className="inline-flex items-center gap-1 text-gray-600 dark:text-gray-400 hover:text-amber-600 dark:hover:text-amber-400 transition-colors mb-4"
          >
            <svg
              className="w-4 h-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M10 19l-7-7m0 0l7-7m-7 7h18"
              />
            </svg>
            Back to API Keys
          </Link>

          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
            {isEditing ? "Edit API key" : "Add new API key"}
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            {isEditing
              ? "Update your API key configuration"
              : "Configure a new AI provider API key"}
          </p>
        </div>

        {errors.length > 0 && (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200/50 dark:border-red-800/50 rounded-lg p-4 mb-6">
            <div className="flex items-start gap-3">
              <svg
                className="w-5 h-5 text-red-600 dark:text-red-400 mt-0.5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <div>
                <p className="font-medium text-red-700 dark:text-red-300 mb-1">
                  Please fix the following errors:
                </p>
                <ul className="list-disc list-inside text-sm text-red-600 dark:text-red-400">
                  {errors.map((error, idx) => (
                    <li key={idx}>{error}</li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          <Card className="bg-white/80 dark:bg-gray-800/50 backdrop-blur-sm border border-gray-200/50 dark:border-gray-700/50">
            <CardHeader className="pb-2">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                Provider configuration
              </h3>
            </CardHeader>
            <CardBody className="space-y-5">
              <div>
                <Input
                  type="text"
                  label="Provider"
                  placeholder="e.g. OpenAI, Anthropic, Groq, OpenRouter"
                  value={data.provider}
                  isRequired={true}
                  variant="bordered"
                  onChange={(e) => setData("provider", e.target.value)}
                  classNames={{
                    inputWrapper:
                      "border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600",
                  }}
                  description="Enter your AI provider name or use a preset below"
                  startContent={
                    <svg
                      className="w-4 h-4 text-gray-400"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth={2}
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                      />
                    </svg>
                  }
                />
                <div className="flex flex-wrap gap-2 mt-3">
                  <p className="text-xs text-gray-500 dark:text-gray-400 w-full mb-1">
                    Quick presets:
                  </p>
                  <Tooltip content="Auto-fill OpenAI settings">
                    <Button
                      variant="flat"
                      size="sm"
                      color="success"
                      onPress={() => handlePreset("openai")}
                      startContent={
                        <svg
                          className="w-3.5 h-3.5"
                          viewBox="0 0 24 24"
                          fill="currentColor"
                        >
                          <path d="M22.2819 9.8211a5.9847 5.9847 0 0 0-.5157-4.9108 6.0462 6.0462 0 0 0-6.5098-2.9A6.0651 6.0651 0 0 0 4.9807 4.1818a5.9847 5.9847 0 0 0-3.9977 2.9 6.0462 6.0462 0 0 0 .7427 7.0966 5.98 5.98 0 0 0 .511 4.9107 6.051 6.051 0 0 0 6.5146 2.9001A5.9847 5.9847 0 0 0 13.2599 24a6.0557 6.0557 0 0 0 5.7718-4.2058 5.9894 5.9894 0 0 0 3.9977-2.9001 6.0557 6.0557 0 0 0-.7475-7.0729zm-9.022 12.6081a4.4755 4.4755 0 0 1-2.8764-1.0408l.1419-.0804 4.7783-2.7582a.7948.7948 0 0 0 .3927-.6813v-6.7369l2.02 1.1686a.071.071 0 0 1 .038.052v5.5826a4.504 4.504 0 0 1-4.4945 4.4944zm-9.6607-4.1254a4.4708 4.4708 0 0 1-.5346-3.0137l.142.0852 4.783 2.7582a.7712.7712 0 0 0 .7806 0l5.8428-3.3685v2.3324a.0804.0804 0 0 1-.0332.0615L9.74 19.9502a4.4992 4.4992 0 0 1-6.1408-1.6464zM2.3408 7.8956a4.485 4.485 0 0 1 2.3655-1.9728V11.6a.7664.7664 0 0 0 .3879.6765l5.8144 3.3543-2.0201 1.1685a.0757.0757 0 0 1-.071 0l-4.8303-2.7865A4.504 4.504 0 0 1 2.3408 7.872zm16.5963 3.8558L13.1038 8.364 15.1192 7.2a.0757.0757 0 0 1 .071 0l4.8303 2.7913a4.4944 4.4944 0 0 1-.6765 8.1042v-5.6772a.79.79 0 0 0-.407-.667zm2.0107-3.0231l-.142-.0852-4.7735-2.7818a.7759.7759 0 0 0-.7854 0L9.409 9.2297V6.8974a.0662.0662 0 0 1 .0284-.0615l4.8303-2.7866a4.4992 4.4992 0 0 1 6.6802 4.66zM8.3065 12.863l-2.02-1.1638a.0804.0804 0 0 1-.038-.0567V6.0742a4.4992 4.4992 0 0 1 7.3757-3.4537l-.142.0805L8.704 5.459a.7948.7948 0 0 0-.3927.6813zm1.0976-2.3654l2.602-1.4998 2.6069 1.4998v2.9994l-2.5974 1.4997-2.6067-1.4997Z" />
                        </svg>
                      }
                    >
                      OpenAI
                    </Button>
                  </Tooltip>
                  <Tooltip content="Auto-fill Anthropic settings">
                    <Button
                      variant="flat"
                      size="sm"
                      color="warning"
                      onPress={() => handlePreset("anthropic")}
                      startContent={
                        <svg
                          className="w-3.5 h-3.5"
                          viewBox="0 0 24 24"
                          fill="currentColor"
                        >
                          <path d="M13.827 3.52h3.603L24 20.48h-3.603L13.827 3.52zm-7.258 0h3.603l6.571 16.96h-3.603L6.569 3.52zm3.259 11.632H5.655l-1.456 3.788H.6L6.569 3.52l3.259 11.632z" />
                        </svg>
                      }
                    >
                      Anthropic
                    </Button>
                  </Tooltip>
                  <Tooltip content="Auto-fill Groq settings">
                    <Button
                      variant="flat"
                      size="sm"
                      color="secondary"
                      onPress={() => handlePreset("groq")}
                    >
                      Groq
                    </Button>
                  </Tooltip>
                  <Tooltip content="Auto-fill OpenRouter settings">
                    <Button
                      variant="flat"
                      size="sm"
                      color="default"
                      onPress={() => handlePreset("openrouter")}
                    >
                      OpenRouter
                    </Button>
                  </Tooltip>
                </div>
              </div>

              <Input
                type="text"
                label="API URL"
                placeholder="https://api.openai.com/v1/chat/completions"
                value={data.url}
                isRequired={true}
                variant="bordered"
                onChange={(e) => setData("url", e.target.value)}
                classNames={{
                  inputWrapper:
                    "border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600",
                }}
                description="The API endpoint URL for the provider"
                startContent={
                  <svg
                    className="w-4 h-4 text-gray-400"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1"
                    />
                  </svg>
                }
              />

              <Input
                type="text"
                label="Model name"
                placeholder="e.g. gpt-4o, claude-sonnet-4-20250514"
                value={data.ai_model_name}
                isRequired={true}
                variant="bordered"
                onChange={(e) => setData("ai_model_name", e.target.value)}
                classNames={{
                  inputWrapper:
                    "border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600",
                }}
                description="The specific model identifier to use"
                startContent={
                  <svg
                    className="w-4 h-4 text-gray-400"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                    />
                  </svg>
                }
              />
            </CardBody>
          </Card>

          <Card className="bg-white/80 dark:bg-gray-800/50 backdrop-blur-sm border border-gray-200/50 dark:border-gray-700/50">
            <CardHeader className="pb-2">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                Authentication
              </h3>
            </CardHeader>
            <CardBody className="space-y-5">
              <Input
                type={showApiKey ? "text" : "password"}
                label="API Key"
                placeholder={
                  isEditing
                    ? "Enter new key to update (leave blank to keep current)"
                    : "sk-..."
                }
                value={data.api_key}
                onChange={(e) => setData("api_key", e.target.value)}
                isRequired={!isEditing}
                variant="bordered"
                classNames={{
                  input: "font-mono",
                  inputWrapper:
                    "border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600",
                }}
                description={
                  isEditing
                    ? "Leave blank to keep current key, or enter a new key to update"
                    : "Your API key will be encrypted and stored securely"
                }
                startContent={
                  <svg
                    className="w-4 h-4 text-gray-400"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z"
                    />
                  </svg>
                }
                endContent={
                  <button
                    type="button"
                    onClick={() => setShowApiKey(!showApiKey)}
                    className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                  >
                    {showApiKey ? (
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        strokeWidth={2}
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21"
                        />
                      </svg>
                    ) : (
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        strokeWidth={2}
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                        />
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                        />
                      </svg>
                    )}
                  </button>
                }
              />

              <Input
                type="text"
                label="Nickname (optional)"
                placeholder="e.g., My Personal Key, Production Key"
                value={data.nickname}
                onChange={(e) => setData("nickname", e.target.value)}
                variant="bordered"
                classNames={{
                  inputWrapper:
                    "border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600",
                }}
                description="A friendly name to help you identify this key"
                startContent={
                  <svg
                    className="w-4 h-4 text-gray-400"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z"
                    />
                  </svg>
                }
              />
            </CardBody>
          </Card>

          <Card className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200/50 dark:border-amber-800/50">
            <CardBody className="p-5">
              <div className="flex items-start gap-3">
                <svg
                  className="w-5 h-5 text-amber-600 dark:text-amber-400 mt-0.5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                  />
                </svg>
                <div>
                  <h3 className="text-sm font-semibold text-amber-900 dark:text-amber-300 mb-1">
                    Security Note
                  </h3>
                  <p className="text-sm text-amber-800 dark:text-amber-400">
                    Keep your API keys secure and never share them publicly.
                    Your keys are encrypted using AES-256 in the database and
                    only decrypted when needed to make API requests.
                  </p>
                </div>
              </div>
            </CardBody>
          </Card>

          <div className="flex gap-4">
            <Button
              type="submit"
              size="lg"
              isLoading={processing}
              className="flex-1 bg-gradient-to-r from-amber-500 to-orange-600 text-white font-semibold shadow-lg shadow-amber-500/25 hover:shadow-amber-500/40"
              startContent={
                !processing && (
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                )
              }
            >
              {processing
                ? "Saving..."
                : isEditing
                  ? "Update API key"
                  : "Add API key"}
            </Button>
            <Button
              as={InertiaLink}
              href="/api-keys"
              variant="flat"
              size="lg"
            >
              Cancel
            </Button>
          </div>
        </form>
      </div>
    </Layout>
  );
}
